import { Box } from '@wix/design-system';
import { Action } from 'history';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigationType } from 'react-router-dom';

import { Loader } from '../../../common/components/Loader';
import { useFetchArticle } from '../../../common/hooks/useFetchArticle';
import { useSkipFirstEffect } from '../../../common/hooks/useSkipFirstEffect';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import { selectArticleState, selectFullPageSidebarExtended } from '../../../common/selectors/helpCenter';
import { useAppDispatch } from '../../../common/store';
import { logger } from '../../../util/logger';
import { renderModel } from '../../../util/renderModel';
import { ArticleContainer } from '../ArticleContainer';
import { ArticleSidebarHeader } from './ArticleSidebarHeader';

const isMobile = renderModel.isMobile();

export const ArticleSidebar: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigationType = useNavigationType();
  const { article, error, isLoading } = useSelector(selectArticleState);
  const [subtitleText, setSubtitleText] = useState<undefined | string>(undefined);
  const [backArticlesCount, setBackArticlesCount] = useState(0);

  const onTitleHiddenChange = useCallback(
    (value: boolean) => setSubtitleText(value ? article?.title : undefined),
    [article],
  );

  useFetchArticle();

  useSkipFirstEffect(() => {
    if (navigationType === Action.Push) {
      return setBackArticlesCount(backArticlesCount + 1);
    }

    if (navigationType === Action.Pop) {
      return setBackArticlesCount(backArticlesCount - 1);
    }
  }, [location]);

  useEffect(() => {
    dispatch(helpCenterSlice.actions.setSidebarOpened(true));

    return () => {
      dispatch(helpCenterSlice.actions.setSidebarOpened(false));
    };
  }, []);

  if (error) {
    // TODO(bar): Need to handle error in side bar.
    logger.error({ error: new Error(`Failed to fetch Article: ${error}`), message: 'ArticleSidebar error' });
  }

  if (!article || isLoading) {
    return <Loader dataHook="article__loader" height="100%" width="100%" verticalAlign="middle" />;
  }

  return (
    <Box direction="vertical" width="100%" align="center">
      <ArticleSidebarHeader subtitleText={subtitleText} showBackButton={backArticlesCount > 0 || isMobile} />
      <ArticleContainer
        article={article}
        onTitleHiddenChange={onTitleHiddenChange}
        openCategoriesExternally
        sidebar
      />
    </Box>
  );
};
